import { isNil, keyBy, mapValues, uniq } from "lodash-es";
import moment, { MomentInput } from "moment";

import { TypeHelper } from "./type";

export class ArrayHelper {
  public static isArray(value: any): value is any[] {
    return Array.isArray(value);
  }

  /** Checks if array is empty.
   *  NB: isEmpty is not used as it treats number, boolean, Date as empty values.
   */
  public static isEmpty<T, TArray extends Array<T>>(arr: Nil<TArray>): arr is Nil<TArray> {
    return TypeHelper.isEmptyArray(arr);
  }

  /** Checks if array is not empty.
   *  NB: isEmpty is not used as it treats number, boolean, Date as empty values.
   */
  public static isNotEmpty<T, TArray extends Array<T>>(arr: Nil<TArray>): arr is TArray {
    return TypeHelper.isNotEmptyArray(arr);
  }

  public static undefinedIfEmpty<T, TArray extends Array<T>>(arr: Nil<TArray>): TArray | undefined {
    return TypeHelper.isEmptyArray(arr) ? undefined : arr;
  }

  public static nullIfEmpty<T, TArray extends Array<T>>(arr: Nil<TArray>): TArray | null {
    return TypeHelper.isEmptyArray(arr) ? null : arr;
  }

  /** Sorts array using Array.sort(). */
  public static sortByDatetime<TItem>(
    arr?: TItem[] | null,
    field?: keyof TItem,
    order: "asc" | "desc" = "asc",
  ): void {
    if (!arr || !field) {
      return;
    }

    arr.sort((a, b) => {
      if (order === "asc") {
        return moment(a[field] as MomentInput).isSameOrBefore(moment(b[field] as MomentInput))
          ? -1
          : 1;
      } else if (order === "desc") {
        return moment(a[field] as MomentInput).isSameOrAfter(moment(b[field] as MomentInput))
          ? -1
          : 1;
      } else {
        return 0;
      }
    });
  }

  /** Replaces array element by predicate (changes the original array). */
  public static replaceByPredicate<TItem>(
    arr: TItem[] | null | undefined,
    predicate: (item: TItem) => boolean,
    newItem: TItem,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    const index = arr.findIndex(predicate);
    index >= 0 && arr.splice(index, 1, newItem);
    return arr;
  }

  /** Replaces array element by predicate (changes the original array). */
  public static replaceByPredicateWithGetter<TItem>(
    arr: TItem[] | null | undefined,
    predicate: (item: TItem) => boolean,
    getNewItem: (params: { oldItem: TItem; oldItemIndex: number }) => TItem,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    const index = arr.findIndex(predicate);
    if (index >= 0) {
      const newItem = getNewItem({ oldItem: arr[index], oldItemIndex: index });
      arr.splice(index, 1, newItem);
    }
    return arr;
  }

  /** Replaces array element by index (changes the original array). */
  public static replaceByIndex<TItem>(
    arr: TItem[] | null | undefined,
    index: number,
    newItem: TItem,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    index >= 0 && arr.splice(index, 1, newItem);
    return arr;
  }

  /** Swaps array elements by index (changes the original array). */
  public static swapItemsByIndex<TItem>(arr: TItem[], indexA: number, indexB: number): TItem[] {
    if (isNil(arr)) {
      return arr;
    }
    if (indexA <= -1 || indexB <= -1 || indexA >= arr.length || indexB >= arr.length) {
      throw new Error("Invalid index.");
    }
    if (indexA === indexB) {
      return arr;
    }
    const itemA = arr[indexA];
    const itemB = arr[indexB];
    arr[indexA] = itemB;
    arr[indexB] = itemA;
    return arr;
  }

  /** Removes array element by reference equality (changes the original array). */
  public static remove<TItem>(
    arr: TItem[] | null | undefined,
    item: TItem,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    const index = arr.indexOf(item);
    if (index >= 0) {
      arr.splice(index, 1);
    }
    return arr;
  }

  /** Removes array element by index (changes the original array). */
  public static removeByIndex<TItem>(
    arr: TItem[] | null | undefined,
    index: number,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    if (index >= 0) {
      arr.splice(index, 1);
    }
    return arr;
  }

  /** Removes first matched array element by predicate (changes the original array). */
  public static removeByPredicate<TItem>(
    arr: TItem[] | null | undefined,
    predicate: (item: TItem) => boolean,
  ): TItem[] | null | undefined {
    if (isNil(arr)) {
      return arr;
    }
    const index = arr.findIndex(predicate);
    index >= 0 && arr.splice(index, 1);
    return arr;
  }

  public static arrayToMap<TItem extends keyof any>(
    arr?: TItem[],
  ): Partial<Record<TItem, boolean>> | undefined {
    return ((arr &&
      mapValues(
        keyBy(arr, (x) => x),
        (x) => true,
      )) ||
      undefined) as Partial<Record<TItem, boolean>> | undefined;
  }

  /** Checks if all elements are uniform (the same). */
  public static isAllUniform<TItem>(arr: TItem[]): boolean {
    return arr.length == 0 || arr.every((x) => x === arr[0]);
  }

  /** Checks if all elements are uniform by the specified key. */
  public static isAllUniformBy<TItem>(arr: TItem[], keySelector: (item: TItem) => any): boolean {
    return arr.length == 0 || uniq(arr.map((x) => keySelector(x))).length == 1;
  }

  /** Checks if two arrays have the same elements. Ignores order and duplicates. */
  public static haveSameElements<TItem>(arr1: Nil<TItem[]>, arr2: Nil<TItem[]>): boolean {
    const set1 = new Set(arr1 || []);
    const set2 = new Set(arr2 || []);
    if (set1.size !== set2.size) {
      return false;
    }
    for (const item of set1) {
      if (!set2.has(item)) {
        return false;
      }
    }
    return true;
  }

  /** Finds the most frequent item in the array.
   * If multiple items have the same frequency the first is returned.
   * [] -> null
   * [1] -> 1
   * [1, 2] -> 1
   * [1, 2, 2] -> 2
   * [1, 1, 2, 2] -> 1
   */
  public static findFirstMostFrequent<T extends string | number | symbol>(arr: Nil<T[]>): T | null {
    if (!arr || arr.length === 0) {
      return null;
    }

    const countsMap = {} as Record<T, number>;
    let maxCount = 0;
    let mostFrequent: T | null = null;

    for (const item of arr) {
      countsMap[item] = (countsMap[item] || 0) + 1;
      if (countsMap[item] > maxCount) {
        maxCount = countsMap[item];
        mostFrequent = item;
      }
    }

    return mostFrequent;
  }
}
